import { useMemo } from "react";

import { selectVersionConfig } from "fond/api";
import { MapboxStyleLayer } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { selectAllStylesByLayer } from "fond/utils/configurations";
import { useAppSelector } from "fond/utils/hooks";

import { addFilterCondition, transformFilterForDrawMode, transformLayerConfigStylesForDrawing } from "../helper";

import { validationStyles } from "./styles";

/**
 * Type guard to check if a config entity is a LayerConfig with Styles property
 */
const isLayerConfig = (config: unknown): config is LayerConfig => {
  return !!config && typeof config === "object" && "Styles" in config && Array.isArray((config as LayerConfig).Styles);
};

/**
 * Type guard to check if a config entity has Styles (LayerConfig or SublayerConfig)
 */
const hasStyles = (config: unknown): config is { Styles: string[] } => {
  return !!config && typeof config === "object" && "Styles" in config && Array.isArray((config as { Styles: string[] }).Styles);
};

/**
 * Creates validation styles for a specific layer based on its geometry type.
 * Only includes validation styles that match the layer's geometry type.
 *
 * @param layerId - The layer ID to create validation styles for
 * @param geometryType - The geometry type of the layer (Point, LineString, or Polygon)
 * @returns Array of validation styles specific to this layer
 */
const createLayerValidationStyles = (layerId: string, geometryType: LayerConfig["GeometryType"]): MapboxStyleLayer[] => {
  const layerValidationStyles: MapboxStyleLayer[] = [];

  // Return early if geometry type is null or "Null" - no validation styles needed
  if (!geometryType || geometryType === "Null") {
    return layerValidationStyles;
  }

  // Map geometry types to their validation style prefixes
  // Multi-geometry types (MultiPoint, MultiLineString, MultiPolygon) use the same styles as their base types
  const geometryTypeToStylePrefix: Record<string, string[]> = {
    Point: ["gl-draw-point-invalid", "gl-draw-point-stroke-invalid"],
    MultiPoint: ["gl-draw-point-invalid", "gl-draw-point-stroke-invalid"],
    LineString: ["gl-draw-line-invalid"],
    MultiLineString: ["gl-draw-line-invalid"],
    Polygon: ["gl-draw-polygon-fill-invalid", "gl-draw-polygon-stroke-invalid"],
    MultiPolygon: ["gl-draw-polygon-fill-invalid", "gl-draw-polygon-stroke-invalid"],
  };

  const relevantStylePrefixes = geometryTypeToStylePrefix[geometryType];
  if (!relevantStylePrefixes) return layerValidationStyles;

  // Filter validation styles to only those matching this geometry type
  const relevantValidationStyles = validationStyles.filter((style) => relevantStylePrefixes.some((prefix) => style.id.startsWith(prefix)));

  // Create layer-specific copies of these validation styles
  relevantValidationStyles.forEach((style) => {
    // Remove $type from filter since we're creating geometry-specific styles
    let filterConditions: unknown[] = [];
    if (style.filter && Array.isArray(style.filter) && style.filter[0] === "all") {
      // Extract all conditions except $type check
      filterConditions = style.filter.slice(1).filter((condition) => {
        if (!Array.isArray(condition)) return true;
        return condition[0] !== "==" || condition[1] !== "$type";
      });
    }

    // Add layerId check to the filter (using shorthand syntax to match other conditions)
    filterConditions.push(["==", "user_layerId", layerId]);

    layerValidationStyles.push({
      ...style,
      id: `${layerId}-${style.id}`,
      filter: ["all", ...filterConditions],
    });
  });

  return layerValidationStyles;
};

/**
 * Helper to apply active state styling to a Mapbox layer style.
 * When a feature has `active: true`, it will be highlighted in the specified color.
 *
 * @param style - The MapboxStyle to apply active styling to
 * @param activeColor - The color to use for active features (default: "yellow")
 * @returns The paint object with active styling applied
 */
const applyActiveStateStyling = (style: Pick<MapboxStyleLayer, "type" | "paint">, activeColor: string = "yellow"): MapboxStyleLayer["paint"] => {
  const { type, paint } = style;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const paintAny = paint as any; // TODO - lets change this to be more type safe

  switch (type) {
    case "symbol":
      return {
        ...paint,
        "text-color": ["case", ["==", ["get", "active"], "true"], activeColor, paintAny?.["text-color"] || "black"],
        "text-halo-color": ["case", ["==", ["get", "active"], "true"], activeColor, paintAny?.["text-halo-color"] || "black"],
      };
    case "line":
      return {
        ...paint,
        "line-color": ["case", ["==", ["get", "active"], "true"], activeColor, paintAny?.["line-color"] || "black"],
      };
    case "circle":
      return {
        ...paint,
        "circle-color": ["case", ["==", ["get", "active"], "true"], activeColor, paintAny?.["circle-color"] || "black"],
      };
    default:
      return paint;
  }
};

/**
 * Custom hook to get drawing styles for multiple layers from the layer configuration.
 *
 * This hook processes all layer keys at once to comply with React's Rules of Hooks.
 *
 * @param versionId - The version ID to get the layers from
 * @param layerKeys - Array of layer keys (e.g., ["input_layers/address", "input_layers/street"])
 * @returns Map of layerKey to MapboxStyleLayer array, or undefined if not available for a layer
 */
export const useMultiLayerDrawingStyles = (versionId: string, layerKeys: string[]): Map<string, MapboxStyleLayer[] | undefined> => {
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));

  return useMemo(() => {
    const stylesMap = new Map<string, MapboxStyleLayer[] | undefined>();

    if (!config?.Data?.entities) {
      // Return empty map if we don't have the required data
      layerKeys.forEach((key) => stylesMap.set(key, undefined));
      return stylesMap;
    }

    // Process each layer
    layerKeys.forEach((layerKey) => {
      // Search through config.Data.entities to find a Layer or Sublayer with matching Key
      // This avoids dependency on getLayers query which might not include layers with zero features
      const layerConfig = config?.Data.entities
        ? (Object.values(config.Data.entities).find((entity) => {
            const e = entity as LayerConfig | SublayerConfig;
            return (e?.Type === "LAYER" || e?.Type === "SUBLAYER") && e?.Key === layerKey;
          }) as LayerConfig | undefined)
        : undefined;

      // Check if we have a valid LayerConfig with Styles
      if (!isLayerConfig(layerConfig)) {
        stylesMap.set(layerKey, undefined);
        return;
      }

      // Use the existing selector to get all styles for this layer (including children)
      const layerStyles = selectAllStylesByLayer(layerConfig, config);

      // Build a map of style IDs to their sublayer filters
      const styleIdToFilter = new Map<string, MapboxStyleLayer["filter"]>();

      // Add styles from the main layer (no filter)
      layerConfig.Styles.forEach((styleId) => styleIdToFilter.set(styleId, undefined));

      // Add styles from all children (sublayers) with their filters
      if (layerConfig.Children) {
        layerConfig.Children.forEach((childId) => {
          const childConfig = config.Data.entities[childId];
          if (hasStyles(childConfig)) {
            const isSublayer = "Type" in childConfig && childConfig.Type === "SUBLAYER";
            const sublayerFilter =
              isSublayer && "FilterConfiguration" in childConfig ? (childConfig as SublayerConfig).FilterConfiguration?.Mapbox : undefined;

            const transformedFilter = sublayerFilter ? transformFilterForDrawMode(sublayerFilter) : undefined;

            childConfig.Styles.forEach((styleId) => styleIdToFilter.set(styleId, transformedFilter));
          }
        });
      }

      // Convert LayerStyle objects to MapboxStyleLayer with proper IDs and filters
      const mapboxStyles = layerStyles
        .map((layerStyle) => {
          if (!layerStyle.MapboxStyle) return null;

          const sublayerFilter = styleIdToFilter.get(layerStyle.ID);
          const combinedFilter = sublayerFilter ? addFilterCondition(layerStyle.MapboxStyle.filter, sublayerFilter) : layerStyle.MapboxStyle.filter;

          // Apply active state styling (highlights features with active: true in yellow)
          const paint = applyActiveStateStyling(layerStyle.MapboxStyle);

          return {
            ...layerStyle.MapboxStyle,
            id: layerStyle.ID,
            filter: combinedFilter,
            paint,
          } as MapboxStyleLayer;
        })
        .filter(Boolean) as MapboxStyleLayer[];

      if (mapboxStyles.length === 0) {
        stylesMap.set(layerKey, undefined);
        return;
      }

      // Transform for use with drawing tools
      const transformed = transformLayerConfigStylesForDrawing(layerKey, mapboxStyles);

      // Create validation styles for this layer based on its geometry type
      // Try to get geometry type from layerConfig (if available)
      const geometryType = layerConfig.GeometryType;
      const validationStylesForLayer = geometryType ? createLayerValidationStyles(layerKey, geometryType) : [];

      // Combine layer styles with validation styles
      // Validation styles should come after layer-specific styles so they render on top
      const combinedStyles = [...validationStylesForLayer, ...transformed];

      stylesMap.set(layerKey, combinedStyles);
    });

    return stylesMap;
  }, [layerKeys, config]);
};
